import { CircularProgress, Grid, makeStyles } from '@material-ui/core'
import DazzedParagraph14 from 'components/common/text/DazzedParagraph14'
import useRolePermissions from 'hooks/useRolePermissions'
import useShowSnackbar from 'hooks/useShowSnackbar'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useFetchNotesQuery } from 'store/modules/PatientNotes'
import NoteInput from './NoteInput'
import NotesEmptyState from './NotesEmptyState'
import NotesList from './NotesList'
import useNeedsAttentionNoteActions from './useNeedsAttentionNoteActions'

const useStyles = makeStyles({
  notesRoot: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    padding: 20,
    height: '100%',
    backgroundColor: 'white'
  },
  loaderContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }
})

const Notes = () => {
  const classes = useStyles()
  const { t } = useTranslation()
  const { permissions } = useRolePermissions()

  const { patient } = useSelector(state => state.patientsReducer)
  const {
    data: notes,
    currentData: currentPatientNotes,
    isLoading,
    isError
  } = useFetchNotesQuery(
    { patientGrinUserId: patient?.user?.id },
    { skip: !patient?.user?.id, refetchOnMountOrArgChange: true }
  )

  // currentData, not data: while another patient's notes load, data still holds the previous patient's.
  const needsAttentionActions = useNeedsAttentionNoteActions({ notes: currentPatientNotes })

  const noNotes = useMemo(() => notes?.length === 0, [notes])

  useShowSnackbar({ isOpen: isError, message: t('pages.patients.patientSwitchableView.notes.fetchNotesError') })

  return isLoading ? (
    <Grid container alignItems="center" justifyContent="center" style={{ height: '100%', backgroundColor: 'white' }}>
      <Grid item>
        <CircularProgress size={30} id="patient-notes-loader" />
      </Grid>
    </Grid>
  ) : (
    <div className={classes.notesRoot} id="patient-notes">
      <DazzedParagraph14 bold color="var(--text-color-18)">
        {t('pages.patients.patientSwitchableView.notes.title')}
      </DazzedParagraph14>
      {noNotes ? <NotesEmptyState /> : <NotesList notes={notes} needsAttentionActions={needsAttentionActions} />}
      {/* Keyed by patient: a draft, or a reply still in flight, never carries over to the next patient. */}
      {permissions.notes.addNotes && <NoteInput key={patient?.id} needsAttentionActions={needsAttentionActions} />}
    </div>
  )
}

export default Notes
